import { Filter, X } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import useTranslations from "@/shared/lang/useTranslations";
import { formatRail } from "@/shared/utils/railDisplay";

import type { ActivityScope } from "../types/paymentsActivity.types";

export interface ActivityScopeBannerProps {
  scope: ActivityScope;
  onClear: () => void;
}

/**
 * Says which slice of operations a link brought in — the organization, the corridor, the window and which
 * date it applies to — so the list is never read as everything when it is not.
 */
export function ActivityScopeBanner({ scope, onClear }: ActivityScopeBannerProps) {
  const { t, activeLanguage } = useTranslations("paymentsActivity");
  const day = (iso: string | null) =>
    iso
      ? new Intl.DateTimeFormat(activeLanguage, { dateStyle: "medium", timeStyle: "short" }).format(
          new Date(iso),
        )
      : "…";
  const currencies =
    scope.sourceCurrency && scope.currency && scope.sourceCurrency !== scope.currency
      ? `${scope.sourceCurrency} → ${scope.currency}`
      : (scope.currency ?? scope.sourceCurrency);
  const parts = [
    scope.organizationName ?? scope.organizationId ?? t("scope_all_organizations"),
    scope.rail ? formatRail(scope.rail) : null,
    scope.country,
    currencies,
    scope.from || scope.to
      ? t(scope.dateField === "completed" ? "scope_window_completed" : "scope_window_created", {
          from: day(scope.from),
          to: day(scope.to),
        })
      : null,
  ].filter((part): part is string => !!part);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-muted/40 px-4 py-3">
      <p className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <Filter aria-hidden className="size-4 shrink-0 text-muted-foreground" />
        <span className="font-medium text-foreground">{t("scope_title")}</span>
        <span className="text-muted-foreground">{parts.join(" · ")}</span>
      </p>
      <ColocaButton variant="ghost" size="sm" icon={X} onClick={onClear}>
        {t("scope_clear")}
      </ColocaButton>
    </div>
  );
}
